import { useMemo, useState } from "react";
import { buildFormatHeatmap } from "@/lib/formatDiff";
import { Flame, ChevronDown, ChevronUp } from "lucide-react";

/**
 * Word-by-word formatting heatmap. Renders each source word with a color
 * underline showing what the student is *missing* at that position (missing
 * bold / italic / underline / alignment or a wholly missing / extra word).
 *
 * Props:
 *   sourceHtml — original passage HTML
 *   typedHtml  — student's current HTML from RichTextEditor
 *   maxWords   — cap on rendered tokens (default 400) to keep the DOM cheap
 */
const LEGEND = [
  { key: "bold",      label: "Missing bold",       color: "decoration-brand-crimson" },
  { key: "italic",    label: "Missing italic",     color: "decoration-brand-gold" },
  { key: "underline", label: "Missing underline",  color: "decoration-blue-500" },
  { key: "align",     label: "Wrong alignment",    color: "decoration-purple-500" },
  { key: "missing",   label: "Word not typed",     color: "decoration-brand-crimson" },
  { key: "extra",     label: "Extra word typed",   color: "decoration-orange-500" },
];

// tailwind-safe class map (kept as literal classes so JIT keeps them in the bundle)
const UNDERLINE_CLASS = {
  bold:      "underline decoration-brand-crimson decoration-2 underline-offset-4",
  italic:    "underline decoration-brand-gold decoration-2 underline-offset-4",
  underline: "underline decoration-blue-500 decoration-2 underline-offset-4",
  align:     "underline decoration-purple-500 decoration-2 underline-offset-4",
  missing:   "underline decoration-brand-crimson decoration-wavy decoration-2 underline-offset-4",
  extra:     "underline decoration-orange-500 decoration-wavy decoration-2 underline-offset-4",
};

function pickPrimary(mismatches) {
  // Priority: missing/extra > bold > italic > underline > align
  const order = ["missing", "extra", "bold", "italic", "underline", "align"];
  for (const k of order) if (mismatches.includes(k)) return k;
  return null;
}

function tooltipFor(cell) {
  if (!cell.mismatches.length) return "OK";
  const parts = cell.mismatches.map((m) => {
    if (m === "missing") return "not typed";
    if (m === "extra") return "extra word";
    if (m === "align") {
      const want = cell.source?.align || "left";
      const got = cell.typed?.align || "left";
      return `alignment: expected ${want}, got ${got}`;
    }
    return `missing ${m}`;
  });
  return parts.join(" · ");
}

export default function FormatHeatmap({ sourceHtml, typedHtml, maxWords = 400 }) {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const { cells, mismatchCount, totalWords } = useMemo(
    () => buildFormatHeatmap(sourceHtml, typedHtml),
    [sourceHtml, typedHtml],
  );

  if (!totalWords) return null;

  const visible = cells.slice(0, maxWords);
  const filtered = showAll ? visible : visible.filter((c) => c.mismatches.length > 0);

  return (
    <div
      className="mt-3 border-2 border-purple-500/30 rounded-xl bg-purple-500/[0.03]"
      data-testid="legal-format-heatmap"
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="legal-format-heatmap-toggle"
        aria-expanded={expanded}
      >
        <div className="flex items-center gap-2">
          <Flame className="w-4 h-4 text-purple-500" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-purple-500">
            Format heatmap
          </span>
          <span
            className="text-xs font-mono font-black text-foreground"
            data-testid="legal-format-heatmap-count"
          >
            {mismatchCount}
            <span className="text-muted-foreground font-normal"> / {totalWords} words off</span>
          </span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {/* Legend */}
          <div className="mb-3 flex flex-wrap gap-2">
            {LEGEND.map((l) => (
              <span key={l.key} className="inline-flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
                <span className={`inline-block w-4 border-b-2 pb-0.5 ${l.color}`}>&nbsp;</span>
                {l.label}
              </span>
            ))}
          </div>

          {/* Toggle */}
          <div className="mb-2 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">
              {showAll ? "Showing all words" : "Showing mismatches only"}
            </div>
            <button
              type="button"
              onClick={() => setShowAll((v) => !v)}
              className="text-[10px] uppercase tracking-widest font-bold text-purple-500 hover:text-purple-400"
              data-testid="legal-format-heatmap-showall"
            >
              {showAll ? "Only mismatches" : "Show all"}
            </button>
          </div>

          {filtered.length === 0 ? (
            <div className="py-6 text-center text-xs uppercase tracking-widest font-bold text-brand-emerald" data-testid="legal-format-heatmap-clean">
              ✓ No formatting mismatches so far
            </div>
          ) : (
            <div className="font-mono text-sm leading-loose max-h-56 overflow-y-auto pr-2">
              {filtered.map((cell) => {
                const primary = pickPrimary(cell.mismatches);
                const cls = primary ? UNDERLINE_CLASS[primary] : "";
                const label = cell.source?.word || cell.typed?.word || "·";
                return (
                  <span
                    key={cell.index}
                    className={`inline-block mr-2 mb-1 px-1 ${cls}`}
                    title={tooltipFor(cell)}
                    data-testid={`legal-heatmap-word-${cell.index}`}
                    data-mismatches={cell.mismatches.join(",") || "ok"}
                  >
                    {label}
                  </span>
                );
              })}
              {cells.length > maxWords && (
                <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground ml-2">
                  … {cells.length - maxWords} more words trimmed
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
